import { createSelector } from '@reduxjs/toolkit';
import { name } from 'app/modules/dataSettings/sliceDataSettings';
import {
  DataSettingsKeyType,
  OrgDataSettingsConfig,
  Unit21DataClassifier,
  CustomDataSettingsConfigResponse,
  NativeDataSettingsConfigResponse,
  DataSettingFieldType,
  SemanticTypes,
} from 'app/modules/dataSettings/responses';
import {
  dataSettingsByClassifierHelper,
  filterDataSettings,
  fingerprintCasingHelper,
  getDataSettingIdFromSelectValue,
  getDataSettingSelectValue,
  keyPathToLabel,
} from 'app/modules/dataSettings/utils';
import { useGetTypeClassifications } from 'app/modules/typeClassification/queries/useGetTypeClassifications';
import { CLASSIFIER_TO_TYPE_NATIVE_KEY } from 'app/modules/typeClassification/constants';
import { useMemo } from 'react';
import {
  TxnEventTypeClassification,
  TypeClassificationMapping,
} from 'app/modules/typeClassification/types';
import { useSelector } from 'react-redux';
import { DEVICE_INTELLIGENCE_FIELD_ORDER } from 'app/modules/dataSettings/constants';

// -- root state selectors --

export const selectDataSettingsState = (state: RootState) => state[name];

export const selectDataSettingsStatus = (state: RootState) =>
  state[name].dataSettings.status;

export const selectDataSettings = createSelector(
  selectDataSettingsState,
  (state) => {
    return state.dataSettings.status === 'COMPLETE'
      ? state.dataSettings.data
      : ([] as OrgDataSettingsConfig[]);
  },
);

export const selectDataSettingsById = createSelector(
  selectDataSettingsState,
  (state) => state.dataSettingsById,
);

export const selectDataSettingsByNativeKey = createSelector(
  selectDataSettingsState,
  (state) => state.dataSettingsByNativeKey,
);

export const selectUiAvailableDataSettings = createSelector(
  selectDataSettingsState,
  (state) => state.uiAvailableDataSettings,
);

export const selectDataSettingsFilters = createSelector(
  selectDataSettingsState,
  (state) => state.filters,
);

export const selectDataSettingsOverlay = createSelector(
  selectDataSettingsState,
  (state) => state.overlay,
);

// -- derived selectors --

export const selectCustomDataSettings = createSelector(
  selectDataSettings,
  (dataSettings) =>
    dataSettings.filter((ds) => ds.key_type === DataSettingsKeyType.CUSTOM),
);

export const selectNativeDataSettings = createSelector(
  selectDataSettings,
  (dataSettings) =>
    dataSettings.filter((ds) => ds.key_type === DataSettingsKeyType.NATIVE),
);

export const selectFilteredDataSettings = createSelector(
  selectDataSettings,
  selectDataSettingsFilters,
  (data, filters) => {
    return filterDataSettings(data, filters);
  },
);

export const selectDataSettingsByClassifier = createSelector(
  selectDataSettings,
  (data) => dataSettingsByClassifierHelper(data),
);

export const selectUiAvailableDataSettingsById = createSelector(
  selectUiAvailableDataSettings,
  (dataSettings) =>
    dataSettings.reduce(
      (acc, ds) => {
        acc[ds.id] = ds;
        return acc;
      },
      {} as Record<number, OrgDataSettingsConfig>,
    ),
);

export const selectUiAvailableDataSettingsByClassifier = createSelector(
  selectUiAvailableDataSettings,
  (data) => dataSettingsByClassifierHelper(data),
);

export const selectCustomDataSettingsByClassifier = createSelector(
  selectDataSettings,
  (data) =>
    dataSettingsByClassifierHelper(data, true) as Record<
      Unit21DataClassifier,
      CustomDataSettingsConfigResponse[]
    >,
);

export const selectCustomDataSettingsByClassifierAndSemanticType =
  createSelector(
    selectCustomDataSettingsByClassifier,
    (
      _,
      options: {
        classifier: Unit21DataClassifier;
        semanticType: SemanticTypes;
      },
    ) => options,
    (
      customDataSettings: Record<Unit21DataClassifier, OrgDataSettingsConfig[]>,
      { classifier, semanticType },
    ) => {
      return customDataSettings[classifier].filter(
        (data) => data.semantic_type === semanticType,
      );
    },
  );

export const selectEventCustomDataSettings = createSelector(
  selectCustomDataSettingsByClassifier,
  (customDataSettings) => customDataSettings[Unit21DataClassifier.EVENT] ?? [],
);

export const selectFfipEnrichmentTargetOptions = createSelector(
  selectDataSettings,
  (dataSettings) =>
    dataSettings
      .filter(
        (dataSetting) =>
          dataSetting.unit21_data_classifier === Unit21DataClassifier.EVENT &&
          Boolean(dataSetting.ffip_matchable_field_name),
      )
      .map((dataSetting) => ({
        text: `${
          dataSetting.key_type === DataSettingsKeyType.NATIVE
            ? 'Unit21'
            : 'Custom data'
        }: ${keyPathToLabel(dataSetting)}`,
        value: getDataSettingSelectValue(dataSetting.id),
      })),
);

export const selectFfipEnrichmentTargetDataSettingIds = createSelector(
  selectFfipEnrichmentTargetOptions,
  (options) =>
    new Set(
      options
        .map(({ value }) => getDataSettingIdFromSelectValue(value))
        .filter((id): id is number => id !== undefined),
    ),
);

export const selectCustomDataTableLabelsByClassifier = createSelector(
  selectCustomDataSettingsByClassifier,
  (customDataSettings) => {
    return Object.entries(customDataSettings).reduce(
      (acc, [classifier, configs]) => {
        acc[classifier] = configs.reduce((accumulator, config) => {
          // top level keys only
          if (config.key_path.length === 1) {
            accumulator[config.key_path[0]] = {
              label: config.user_facing_label || config.key_path[0],
              description: config.description,
            };
          }
          return accumulator;
        }, {});
        return acc;
      },
      {} as Record<
        Unit21DataClassifier,
        Record<string, { label: string; description: string }>
      >,
    );
  },
);

const useTxnEventTypeClassification = (
  txnTypeClassification: TxnEventTypeClassification,
) => {
  const { data: typeClassifications } = useGetTypeClassifications();
  const transactionDataSettingsByNativeKey = useSelector(
    selectTransactionDataSettingsByNativeKey,
  );
  const txnTypeDataSettingId =
    transactionDataSettingsByNativeKey?.[
      CLASSIFIER_TO_TYPE_NATIVE_KEY[Unit21DataClassifier.EVENT]
    ]?.id;
  return useMemo(() => {
    if (!txnTypeDataSettingId) return [];
    if (!typeClassifications) return [];
    return typeClassifications.data.filter(
      ({ classification, data_setting_id: dataSettingId }) =>
        classification === txnTypeClassification &&
        dataSettingId === txnTypeDataSettingId,
    );
  }, [txnTypeClassification, txnTypeDataSettingId, typeClassifications]);
};
export const useCheckClassifications = (): TypeClassificationMapping[] => {
  return useTxnEventTypeClassification(TxnEventTypeClassification.CHECK);
};
export const useAchClassifications = (): TypeClassificationMapping[] => {
  return useTxnEventTypeClassification(TxnEventTypeClassification.ACH);
};

export const selectEntityDataSettingsByNativeKey = createSelector(
  selectDataSettingsByNativeKey,
  (state) => state?.[Unit21DataClassifier.ENTITY] ?? {},
);

export const selectInstrumentDataSettingsByNativeKey = createSelector(
  selectDataSettingsByNativeKey,
  (state) => state?.[Unit21DataClassifier.INSTRUMENT] ?? {},
);

export const selectTransactionDataSettingsByNativeKey = createSelector(
  selectDataSettingsByNativeKey,
  (state) => state?.[Unit21DataClassifier.EVENT] ?? {},
);

export const selectActionEventDataSettingsByNativeKey = createSelector(
  selectDataSettingsByNativeKey,
  (state) => state?.[Unit21DataClassifier.ACTION_EVENT] ?? {},
);

export const selectTransactionFingerprintSettings = createSelector(
  selectNativeDataSettings,
  (nativeDataSettings) => {
    const filtered = nativeDataSettings.reduce(
      (acc, ds) => {
        if (
          ds.unit21_data_classifier === Unit21DataClassifier.EVENT &&
          ds.native_key.startsWith('fingerprint_')
        ) {
          acc[ds.native_key] = {
            ...ds,
            native_field: fingerprintCasingHelper(ds.native_field || ''),
          };
        }
        return acc;
      },
      {} as Record<string, NativeDataSettingsConfigResponse>,
    );

    return DEVICE_INTELLIGENCE_FIELD_ORDER.map<NativeDataSettingsConfigResponse>(
      (key) => filtered[key],
    ).filter((v) => !!v);
  },
);

export const selectActionEventFingerprintSettings = createSelector(
  selectNativeDataSettings,
  (nativeDataSettings) => {
    const filtered = nativeDataSettings.reduce(
      (acc, ds) => {
        if (
          ds.unit21_data_classifier === Unit21DataClassifier.ACTION_EVENT &&
          ds.native_key.startsWith('fingerprint_')
        ) {
          acc[ds.native_key] = {
            ...ds,
            native_field: fingerprintCasingHelper(ds.native_field || ''),
          };
        }
        return acc;
      },
      {} as Record<string, NativeDataSettingsConfigResponse>,
    );

    return DEVICE_INTELLIGENCE_FIELD_ORDER.map<NativeDataSettingsConfigResponse>(
      (key) => filtered[key],
    ).filter((v) => !!v);
  },
);

export const selectEntityPerspectiveEnums = createSelector(
  selectCustomDataSettingsByClassifier,
  (customDataSettings) =>
    customDataSettings[Unit21DataClassifier.EVENT]
      ?.filter(
        (data): data is CustomDataSettingsConfigResponse =>
          data.data_type === DataSettingFieldType.ENTITY_REFERENCE &&
          data.perspective !== undefined,
      )
      ?.map((p) => p.perspective!) || [],
);

export const selectInstrumentPerspectiveEnums = createSelector(
  selectCustomDataSettingsByClassifier,
  (customDataSettings) =>
    customDataSettings[Unit21DataClassifier.EVENT]
      ?.filter(
        (data): data is CustomDataSettingsConfigResponse =>
          data.data_type === DataSettingFieldType.INSTRUMENT_REFERENCE &&
          data.perspective !== undefined,
      )
      ?.map((p) => p.perspective!) || [],
);

export const selectInstrumentReferenceDataSettings = createSelector(
  selectDataSettings,
  (dataSettings) =>
    dataSettings.filter(
      (dataSetting) =>
        dataSetting.unit21_data_classifier === Unit21DataClassifier.EVENT &&
        dataSetting.data_type === DataSettingFieldType.INSTRUMENT_REFERENCE,
    ),
);
